import React from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  PieProps,
  Cell,
  Label,
} from 'recharts';
import { BaseChart } from '../../BaseChart';
import { CommonChartProps } from '../../types';
import { getAdjustedOpacity, getColorForKey } from '../../utils';

export type ExtendedPieProps = React.PropsWithoutRef<PieProps>;

export type PieChartProps<TData> = Omit<
  CommonChartProps<
    TData,
    React.ComponentProps<typeof RechartsPieChart> & { data?: TData[] },
    ExtendedPieProps
  >,
  | 'xAxisProps'
  | 'yAxisProps'
  | 'showXAxis'
  | 'showYAxis'
  | 'cartesianGridProps'
  | 'showCartesianGrid'
> & {
  centerTitle?: string;
  centerSubtitle?: string;
};

export const PieChart = <TData,>({
  data,
  chartProps,
  centerTitle,
  centerSubtitle,
  ...restProps
}: PieChartProps<TData>) => {
  return (
    <BaseChart
      {...restProps}
      data={data}
      chartType="pie"
      ChartComponent={RechartsPieChart}
      legendProps={{
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
        ...restProps.legendProps,
      }}
      renderSeries={({ normalizedSeries, colors, activeIndex }) => {
        const { dataKey, color, ...restSeriesProps } = normalizedSeries[0];
        const nameKey = restProps.categoryKey as Extract<keyof TData, string>;
        const normalizedDataKey = dataKey as Extract<keyof TData, string>;

        return (
          <Pie
            {...restSeriesProps}
            data={data}
            fill={color}
            dataKey={normalizedDataKey}
            nameKey={nameKey}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${entry[nameKey]}`}
                fill={
                  colors?.[index] || getColorForKey(`${index}${entry[nameKey]}`)
                }
                stroke={
                  colors?.[index] || getColorForKey(`${index}${entry[nameKey]}`)
                }
                name={`${entry[nameKey]}`}
                fillOpacity={getAdjustedOpacity(
                  activeIndex,
                  index,
                  restSeriesProps.fillOpacity,
                )}
                strokeOpacity={getAdjustedOpacity(
                  activeIndex,
                  index,
                  restSeriesProps.strokeOpacity,
                )}
              >
                {`${entry[normalizedDataKey as keyof TData]}`}
              </Cell>
            ))}
            {(centerTitle || centerSubtitle) && (
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        {centerTitle && (
                          <tspan
                            x={viewBox.cx}
                            y={viewBox.cy}
                            className="text-3xl leading-tight font-semibold"
                          >
                            {centerTitle}
                          </tspan>
                        )}
                        {centerSubtitle && (
                          <tspan
                            x={viewBox.cx}
                            y={(viewBox.cy || 0) + 24}
                            className="fill-content-muted"
                          >
                            {centerSubtitle}
                          </tspan>
                        )}
                      </text>
                    );
                  }
                  return null;
                }}
              />
            )}
          </Pie>
        );
      }}
      chartProps={chartProps}
    />
  );
};
